import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { supabase } from '@/lib/supabase';
import { qk } from '@/lib/queryKeys';
import { MODULE_KEYS, type ModuleKey } from '@/access/modules';
import type { Me, MeBranch } from './me.types';

/**
 * העובד המחובר בקריאה אחת ל RPC me() (מיגרציה 007, מסמך התכנון סעיף 5.3).
 * מחזיר null למי שאינו עובד פעיל, וזה מה שמפעיל את מסך "אין גישה".
 */
type MeRow = {
  employee_id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string | null;
  avatar_path: string | null;
  birth_date: string | null;
  start_date: string | null;
  job_title: string | null;
  role_name: string;
  scope: Me['scope'];
  primary_branch_id: string | null;
  branches: MeBranch[];
  permissions: string[];
  modules: Record<string, boolean>;
};

function mapMe(row: MeRow): Me {
  return {
    employeeId: row.employee_id,
    firstName: row.first_name,
    lastName: row.last_name,
    phone: row.phone,
    email: row.email,
    avatarPath: row.avatar_path,
    birthDate: row.birth_date,
    startDate: row.start_date,
    jobTitle: row.job_title,
    roleName: row.role_name,
    scope: row.scope,
    primaryBranchId: row.primary_branch_id,
    branches: row.branches ?? [],
    permissions: row.permissions ?? [],
    modules: Object.fromEntries(
      MODULE_KEYS.map((key) => [key, row.modules?.[key] === true]),
    ) as Record<ModuleKey, boolean>,
  };
}

async function fetchMe(): Promise<Me | null> {
  const { data, error } = await supabase.rpc('me');
  if (error) throw error;
  if (!data) return null;
  return mapMe(data as unknown as MeRow);
}

export function useMeQuery(authUserId: string | undefined) {
  return useQuery({
    queryKey: [...qk.me, authUserId],
    queryFn: fetchMe,
    enabled: Boolean(authUserId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

type MeState = { me: Me | null; isLoading: boolean };

export const MeContext = createContext<MeState>({ me: null, isLoading: false });

export function useMe(): MeState {
  return useContext(MeContext);
}
